import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { formatTime } from '@/utils';
import { certificationGateway } from '@/di-container/common/certification/client';
import _ from 'lodash';
import { useAlert } from '@design-system/pc';
import { ALERT_Z_INDEX } from '@/styles/layout';

export type CertificationThrottleStatus = {
  prevTel: string | number | null;
  countSnap: number;
};

export type Time = {
  text: string;
  value: number;
};

// BEFORE_SEND (인증번호 전송 전) | CERTIFYING (인증번호 전송) | CERTIFIED (인증번호 확인 완료) | EXPIRED (인증시간 만료) | INACCURATE(인증번호 입력 오류) | EXCEEDED (인증시도횟수 초과)
export type CertificationStatus =
  | 'BEFORE_SEND'
  | 'CERTIFYING'
  | 'CERTIFIED'
  | 'TIME_EXPIRED'
  | 'INACCURATE'
  | 'EXCEEDED';

interface UseTelCertification {
  // 인증번호 재요청 가능한 시간 범위 (단위: 초)
  defaultCount?: number;
  // 인증 시도 제한 횟수
  certifyLimit?: number;
  // 카카오 템플릿 코드
  templateCode: string;
}

const DEFAULT_TIME = 180;
const CERTIFY_LIMIT = 30;
const CERTIFICATION_UUID_KEY = 'seq-certification-key';

const useTelCertification = ({
  defaultCount,
  certifyLimit = CERTIFY_LIMIT,
  templateCode,
}: UseTelCertification) => {
  const getDefaultTime = useCallback(() => {
    const _defaultCount = defaultCount ?? DEFAULT_TIME;
    return {
      text: formatTime(_defaultCount),
      value: defaultCount || _defaultCount,
    };
  }, [defaultCount]);

  const { handleOpen: handleAlertOpen } = useAlert();
  const { getTelCertification, postTelCertification } = certificationGateway();
  const [count, setCount] = useState<Time>(() => getDefaultTime());
  const [status, setStatus] = useState<CertificationStatus>('BEFORE_SEND');
  const throttleStatusRef = useRef<CertificationThrottleStatus>({
    prevTel: null,
    countSnap: 0,
  });

  const isThrottling = throttleStatusRef.current.countSnap - count.value <= 10;

  const handleSend = useCallback(
    async (tel: string | number) => {
      if (
        throttleStatusRef.current.prevTel === tel &&
        status === 'CERTIFYING' &&
        isThrottling
      ) {
        handleAlertOpen({
          content: '잠시 후에 다시 시도 해주세요.',
          subContent:
            '동일한 연락처로 다시 전송하려면\n약 10초의 대기시간이 필요해요.',
          zIndex: ALERT_Z_INDEX,
        });
        return;
      } else {
        throttleStatusRef.current.prevTel = tel;
        throttleStatusRef.current.countSnap = DEFAULT_TIME;
      }

      const uuid = window.sessionStorage.getItem(CERTIFICATION_UUID_KEY);

      if (uuid === null) {
        console.error('[Error] UUID가 비어있습니다.');
        return;
      }

      const response = await postTelCertification.exec({
        tel: `${tel}`,
        templateCode,
        uuid,
      });

      if (
        response.code === 200 &&
        response.data?.cnt &&
        response.data.cnt > 0
      ) {
        if (response.data.cnt > certifyLimit) {
          setStatus('EXCEEDED');
        } else {
          setStatus('CERTIFYING');

          const defaultTime = getDefaultTime();
          setCount(defaultTime);
        }
      } else if (response.code === 11000) {
        // 인증번호를 더 이상 전송 할 수 없습니다. 잠시 후 다시 시도 해주세요.
        setStatus('EXCEEDED');
      } else {
        // 인증번호 신청 실패 시 케이스
        console.error(`[response: ${response?.code}]: ${response?.message}`);
      }

      return response;
    },
    [
      certifyLimit,
      getDefaultTime,
      handleAlertOpen,
      isThrottling,
      postTelCertification,
      status,
      templateCode,
    ],
  );

  // eslint-disable-next-line no-unused-vars
  const handleCertification = useCallback(
    async (tel: string | number, certifiedNumber: string | number) => {
      const _tel = _.toString(tel);
      const certificationNoValue = _.toString(certifiedNumber);
      const uuid = window.sessionStorage.getItem(CERTIFICATION_UUID_KEY);

      if (uuid === null) {
        console.error('[Error] UUID가 비어있습니다.');
        return;
      }

      // 인증번호 전송 API 호출 (params: verifiedNumber)
      const response = await getTelCertification.exec({
        tel: _tel,
        uuid,
        certificationNoValue,
      });

      if (response.code === 200) {
        setStatus('CERTIFIED');
      } else if (response.code === 11002) {
        setStatus('TIME_EXPIRED');
      } else if (response.code === 11001 || response.code === 12100) {
        setStatus('INACCURATE');
      } else {
        console.error(`[response: ${response?.code}]: ${response?.message}`);
      }

      return response;
    },
    [getTelCertification],
  );

  const handleResetStatus = useCallback((status?: CertificationStatus) => {
    setStatus(status || 'BEFORE_SEND');
  }, []);

  /* 
    점유인증을 위한 UUID 는 sessionStorage 만료 기준
    따라서, 페이지 접근 시 최초 생성되며, 브라우저 종료 시 만료
    한샘몰 점유인증 위키: https://hanssem.atlassian.net/wiki/spaces/IT1/pages/1013973143
  */
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hasUUID = window.sessionStorage.getItem(CERTIFICATION_UUID_KEY);
      if (hasUUID === null) {
        window.sessionStorage.setItem(CERTIFICATION_UUID_KEY, uuidv4());
      }
    }
  }, []);

  useEffect(() => {
    let timer: any;

    if (
      (status === 'CERTIFYING' || status === 'INACCURATE') &&
      count &&
      count.value > 0
    ) {
      timer = setInterval(() => {
        setCount(({ text, value }) =>
          value > 0
            ? {
                text: formatTime(value - 1),
                value: value - 1,
              }
            : { text, value },
        );
      }, 1000);
    } else if (count.value === 0) {
      clearInterval(timer);
      setStatus('TIME_EXPIRED');
      setCount(getDefaultTime());
    }

    return () => clearInterval(timer);
  }, [count, getDefaultTime, status]);

  return {
    handleSend,
    handleCertification,
    handleResetStatus,
    count,
    status,
  };
};

export default useTelCertification;
